import { useState, useEffect, useRef, useCallback } from "react";
import { Capacitor, registerPlugin } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import { useAuth } from "./context/AuthContext";

// App unlock for the Android app (same behaviour as the Billing app):
//  - After login, once: a popup asks "Turn on app unlock?" (Turn on / Not now).
//  - If turned on, the app opens with fingerprint, face or phone PIN.
//  - Locks again after 2 minutes in the background.
//  - Signing out clears the choice, so the next login asks again.
// The website is not affected.
const LOCK_AFTER_MS = 2 * 60 * 1000;
const FLAG = "crmAppUnlock"; // null = not asked yet, "on", "off"
const BiometricAuth = registerPlugin("BiometricAuthNative");

const getFlag = () => { try { return localStorage.getItem(FLAG); } catch { return null; } };
const setFlag = (v) => {
  try { if (v === null) localStorage.removeItem(FLAG); else localStorage.setItem(FLAG, v); } catch { /* ignore */ }
};

async function verify(reason) {
  await BiometricAuth.internalAuthenticate({
    reason,
    cancelTitle: "Cancel",
    allowDeviceCredential: true,
    androidTitle: "Unlock ABC CRM",
    androidSubtitle: "Use fingerprint, face or your phone PIN",
  });
}

const s = {
  page: { minHeight: "100vh", background: "#0f0f13", color: "#f3f4f6", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "'Segoe UI', Arial, sans-serif", textAlign: "center", gap: 14 },
  icon: { width: 64, height: 64, borderRadius: 16, background: "linear-gradient(135deg, #6c63ff, #8b7cff)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 30 },
  title: { fontSize: 20, fontWeight: 600 },
  sub: { fontSize: 13, color: "#8f95a8", maxWidth: 320 },
  err: { color: "#ff7b7b", fontSize: 12, maxWidth: 320 },
  btn: { width: "100%", maxWidth: 320, border: "none", borderRadius: 12, padding: "12px 16px", background: "linear-gradient(135deg, #6c63ff, #8b7cff)", color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer" },
  link: { background: "transparent", border: "none", color: "#8f95a8", cursor: "pointer", fontSize: 12 },
  overlay: { position: "fixed", inset: 0, zIndex: 100000, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 },
  modal: { width: "100%", maxWidth: 360, background: "#161821", border: "1px solid #2b2f3d", borderRadius: 16, padding: 24, textAlign: "center", color: "#f3f4f6", fontFamily: "'Segoe UI', Arial, sans-serif" },
  modalIcon: { width: 48, height: 48, margin: "0 auto 12px", borderRadius: 12, background: "linear-gradient(135deg, #6c63ff, #8b7cff)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 },
  row: { display: "flex", gap: 8, marginTop: 20 },
  notNow: { flex: 1, padding: "10px 0", borderRadius: 12, border: "none", background: "rgba(255,255,255,0.06)", color: "#cbd0dd", fontSize: 13, fontWeight: 600, cursor: "pointer" },
  turnOn: { flex: 1, padding: "10px 0", borderRadius: 12, border: "none", background: "linear-gradient(135deg, #6c63ff, #8b7cff)", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" },
};

export default function AppLock({ children }) {
  const { user, loading, logout } = useAuth();
  const isNative = Capacitor.isNativePlatform();
  const [locked, setLocked] = useState(() => isNative && getFlag() === "on");
  const [askOpen, setAskOpen] = useState(false);
  const [error, setError] = useState(null);
  const busy = useRef(false);
  const hiddenAt = useRef(null);

  const unlock = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    setError(null);
    try {
      await verify("Unlock ABC CRM");
      setLocked(false);
    } catch {
      setError("Not verified. Try again, or sign in with your password.");
    } finally {
      busy.current = false;
    }
  }, []);

  // Signed out: forget the choice so the next login asks again.
  useEffect(() => {
    if (!isNative || loading || user) return;
    setFlag(null);
    setLocked(false);
    setAskOpen(false);
  }, [isNative, loading, user]);

  // Locked and signed in: prompt straight away.
  useEffect(() => {
    if (isNative && locked && user) unlock();
  }, [isNative, locked, user, unlock]);

  // Signed in, not locked, never asked: offer to turn it on (only if the phone has a screen lock).
  useEffect(() => {
    if (!isNative || !user || locked || getFlag() !== null) return undefined;
    let cancelled = false;
    BiometricAuth.checkBiometry()
      .then((info) => { if (!cancelled && (info.isAvailable || info.deviceIsSecure)) setAskOpen(true); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isNative, user, locked]);

  // Lock again after 2 minutes in the background.
  useEffect(() => {
    if (!isNative) return undefined;
    let handle;
    CapApp.addListener("appStateChange", ({ isActive }) => {
      if (!isActive) {
        hiddenAt.current = Date.now();
        return;
      }
      if (busy.current || hiddenAt.current === null) return;
      const away = Date.now() - hiddenAt.current;
      hiddenAt.current = null;
      if (getFlag() === "on" && away > LOCK_AFTER_MS) setLocked(true);
    }).then((h) => { handle = h; });
    return () => { if (handle) handle.remove(); };
  }, [isNative]);

  const turnOn = async () => {
    setError(null);
    try {
      await verify("Confirm to turn on app unlock");
      setFlag("on");
      setAskOpen(false);
    } catch {
      setError("Could not verify, so app unlock was not turned on.");
    }
  };

  const notNow = () => {
    setFlag("off");
    setAskOpen(false);
    setError(null);
  };

  if (!isNative) return children;

  if (locked && user) {
    return (
      <div style={s.page}>
        <div style={s.icon}>🔒</div>
        <div style={s.title}>App locked</div>
        <div style={s.sub}>Use your fingerprint, face or phone PIN to continue.</div>
        {error ? <div style={s.err}>{error}</div> : null}
        <button style={s.btn} onClick={unlock}>Unlock</button>
        <button style={s.link} onClick={logout}>Sign in with password instead</button>
      </div>
    );
  }

  return (
    <>
      {children}
      {askOpen && user ? (
        <div style={s.overlay}>
          <div style={s.modal}>
            <div style={s.modalIcon}>🔒</div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>Turn on app unlock?</div>
            <div style={{ fontSize: 12, color: "#8f95a8", marginTop: 6 }}>
              Open this app with your fingerprint, face or phone PIN instead of typing your password each time.
            </div>
            {error ? <div style={{ ...s.err, marginTop: 8 }}>{error}</div> : null}
            <div style={s.row}>
              <button style={s.notNow} onClick={notNow}>Not now</button>
              <button style={s.turnOn} onClick={turnOn}>Turn on</button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
